import React from "react";
import PropTypes from "prop-types";
import PersonIcon from "@material-ui/icons/Person";
import RoomIcon from "@material-ui/icons/Room";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_DatePicker from "../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import M_FirmPicker from "../../../Molecules/Common/ProfessionalContacts/M_FirmPicker";
import { LOAN_DETAIL_ROLE_TO_PARTY_ROLE, LOAN_ROLE_DATE_FIELDS } from "../../../../../domain/professionalContacts/professionalContacts.types";
import useStyles from "./ProfessionalDetails.styles";

const epochSecondsToIso = (value) => (value ? new Date(value * 1000).toISOString().slice(0, 10) : null);

/**
 * One role's fields in the Edit Professional Details dialog (§4b): search-select firm picker
 * (selectable-firms only) + Active individual select + Address select (from the chosen firm,
 * defaulted to main/preferred and reset when the firm changes) + that role's date fields.
 */
const RoleEditFields = ({ roleKey, label, required, loanId, assignment, firm, onFirmChange, onContactChange, onAddressChange, onDateChange }) => {
  const classes = useStyles();
  const contact = firm?.contacts?.find((c) => c.id === assignment.contactId) || null;
  const address = firm?.addresses?.find((a) => a.id === assignment.addressId) || null;
  const dateFields = LOAN_ROLE_DATE_FIELDS[roleKey] || [];

  return (
    <div>
      <M_FirmPicker
        id={`role-firm-${roleKey}`}
        label={`${label}${required ? " *" : ""}`}
        loanId={loanId}
        role={LOAN_DETAIL_ROLE_TO_PARTY_ROLE[roleKey]}
        value={assignment.firmId}
        onChange={onFirmChange}
      />
      {firm && (
        <>
          <A_SimpleSelect
            id={`role-contact-${roleKey}`}
            label="Active individual *"
            defaultValue={assignment.contactId}
            options={firm.contacts.map((c) => ({ value: c.id, label: `${c.name || "(unnamed)"}${c.role ? ` (${c.role})` : ""}${c.isMain ? " · main" : ""}` }))}
            optionLabel="label"
            optionValue="value"
            onChange={onContactChange}
            style={{ width: "100%" }}
          />
          <A_SimpleSelect
            id={`role-address-${roleKey}`}
            label="Address *"
            defaultValue={assignment.addressId}
            options={firm.addresses.map((a) => ({
              value: a.id,
              label: `${[a.building, a.street, a.town, a.postcode].filter(Boolean).join(", ")}${a.isPreferred ? " · preferred" : ""}`,
            }))}
            optionLabel="label"
            optionValue="value"
            onChange={onAddressChange}
            style={{ width: "100%" }}
          />
          {contact && (
            <div className={classes.subtle} style={{ margin: "2px 0" }}>
              <PersonIcon fontSize="inherit" /> {contact.name} · {contact.email} · {contact.phone}
            </div>
          )}
          {address && (
            <div className={classes.subtle} style={{ margin: "2px 0 8px" }}>
              <RoomIcon fontSize="inherit" /> {[address.building, address.street, address.town, address.postcode].filter(Boolean).join(", ")}
            </div>
          )}
        </>
      )}
      {dateFields.map(({ key, label: dateLabel }) => (
        <A_DatePicker
          key={key}
          id={`role-date-${roleKey}-${key}`}
          label={dateLabel}
          margin="dense"
          defaultValue={assignment.dates?.[key]}
          onChange={(epochSeconds) => onDateChange(key, epochSecondsToIso(epochSeconds))}
          fullWidth
        />
      ))}
    </div>
  );
};

RoleEditFields.propTypes = {
  roleKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  loanId: PropTypes.string.isRequired,
  assignment: PropTypes.object.isRequired,
  firm: PropTypes.object,
  onFirmChange: PropTypes.func.isRequired,
  onContactChange: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
};

export default RoleEditFields;
